// Histórico de recebimentos do título (motor único) com recibo e estorno por payment_id
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { History, Loader2, Receipt, Undo2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const dt = (v) => (v ? new Date(String(v).length === 10 ? `${v}T12:00:00` : v).toLocaleDateString('pt-BR') : '—');

export default function HistoricoRecebimentos({ titleId, onChanged, canReverse }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [revId, setRevId] = useState(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    if (!titleId) return;
    try {
      const { data: d } = await api.get(`/financial/titulos/${titleId}/payments`);
      setData(d);
    } catch (_) { setData(null); }
  }, [titleId]);

  useEffect(() => { load(); }, [load]);

  const estornar = async (pid) => {
    if (!reason.trim()) { toast.error('Informe o motivo do estorno.'); return; }
    setBusy(true);
    try {
      const { data: d } = await api.post(`/financial/titulos/payments/${pid}/reverse`, { reason });
      toast.success(`Estorno registrado · título ${d.status} · saldo ${brl(d.balance)}`);
      setRevId(null); setReason('');
      load(); onChanged?.();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Não foi possível estornar.');
    } finally { setBusy(false); }
  };

  const recibo = async () => {
    try {
      const r = await api.get(`/financial/titulos/${titleId}/recibo.jpg`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch { toast.error('Não foi possível gerar o recibo.'); }
  };

  if (!data) return null;
  const rows = [...(data.payments || []), ...(data.reversed || [])];

  return (
    <Card data-testid="historico-recebimentos">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="w-5 h-5 text-violet-600" /> Histórico de recebimentos
        </CardTitle>
        <div className="flex flex-wrap gap-3 text-[12px] text-slate-600 pt-1">
          <span>Original <b>{brl(data.original_amount)}</b></span>
          <span>Desconto <b>{brl(data.discount_amount)}</b></span>
          <span>Recebido <b className="text-emerald-700">{brl(data.received)}</b></span>
          <span>Saldo <b className={data.balance > 0 ? 'text-amber-700' : ''}>{brl(data.balance)}</b></span>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {!rows.length && <p className="text-sm text-slate-500">Nenhum recebimento registrado.</p>}
        {rows.map((p, i) => (
          <div key={p.payment_id || `legacy-${i}`}
            className={`rounded-xl border p-3 ${p.reversed ? 'bg-rose-50/60 border-rose-200' : 'bg-white'}`}
            data-testid={`recebimento-${p.payment_id || 'legado'}`}>
            <div className="flex flex-wrap justify-between gap-2">
              <div className="text-sm">
                <p className="font-semibold text-slate-800">
                  {dt(p.paid_at)} · {p.method_label}
                  {p.installments > 1 ? ` ${p.installments}x` : ''}
                  {p.account_name ? ` · ${p.account_name}` : ''}
                  {p.legacy ? ' · recebimento legado' : ''}
                </p>
                <p className="text-[12px] text-slate-500">
                  {brl(p.gross_amount)} · taxa {brl(p.fee_amount)} · líquido {brl(p.net_amount)}
                  {p.settlement_date ? ` · liquidação ${dt(p.settlement_date)}` : ''}
                </p>
                {(p.nsu || p.last4) && (
                  <p className="text-[11px] text-slate-400">
                    {p.brand || ''} {p.last4 ? `****${p.last4}` : ''} {p.nsu ? `· NSU ${p.nsu}` : ''}
                    {p.authorization_code ? ` · aut. ${p.authorization_code}` : ''}
                  </p>
                )}
                {p.reversed && (
                  <p className="text-[12px] text-rose-700 font-medium">
                    Estornado em {dt(p.reversed_at)} · {p.reversal_reason}
                  </p>
                )}
              </div>
              <div className="flex items-start gap-1.5">
                <Button size="sm" variant="outline" onClick={recibo} data-testid={`recibo-${p.payment_id || 'legado'}`}>
                  <Receipt className="w-3.5 h-3.5 mr-1" /> Recibo
                </Button>
                {!p.reversed && !p.legacy && canReverse && (
                  <Button size="sm" variant="ghost" className="text-rose-700"
                    onClick={() => { setRevId(p.payment_id); setReason(''); }}
                    data-testid={`estornar-${p.payment_id}`}>
                    <Undo2 className="w-3.5 h-3.5 mr-1" /> Estornar
                  </Button>
                )}
              </div>
            </div>
            {revId === p.payment_id && (
              <div className="mt-2 flex flex-wrap gap-2 items-center">
                <Input value={reason} onChange={(e) => setReason(e.target.value)} className="flex-1 min-w-[200px]"
                  placeholder="Motivo do estorno (obrigatório)" data-testid="estorno-motivo" />
                <Button size="sm" disabled={busy} className="bg-rose-600 hover:bg-rose-700"
                  onClick={() => estornar(p.payment_id)} data-testid="estorno-confirmar">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar estorno'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setRevId(null)}>Cancelar</Button>
              </div>
            )}
          </div>
        ))}
        {data.balance > 0 && (
          <p className="text-[12px] text-amber-700 flex items-center gap-1.5 pt-1">
            <Wallet className="w-4 h-4" /> Saldo em aberto de {brl(data.balance)} — o título aceita novo recebimento.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
